import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCIcon, RCCRangeSlider, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { IComponentObjectiveTab, IProjectObjective } from "./interface";
import ModalSelectObjective, { IRefSelectObjectiveModals } from "./selectObjective";

const ProjetObjective: React.FC<IComponentObjectiveTab> = ({
	subTitle,
	control,
	error,
	objectivesDefaultValue,
	disabled = false,
	register,
	clearErrors,
	names
}) => {
	const { translate } = useTranslation();
	const modalSelectObjectiveRef = useRef<IRefSelectObjectiveModals>(null);

	const [ loading, setLoading ] = useState(false);

	const { 
		fields: fieldsObjectives, 
		remove: removeObjectives,
		append: appendObjectives
	} = useFieldArray({
		control,
		name: names.nameOfObject
	});

	const removeObjective = (index: number) => {
		clearErrors(names.nameOfObject);			
		removeObjectives(index);
	};

	const getObjectives = async (dataX: string[]) => {
		const splitedData = dataX.join(",");

		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Objectives/ListByIds",
				urlParameters: {
					listOfIds: splitedData
				}
			});

			const objectivesData = data?.map((metba003: any) => {
				const projetObjective: IProjectObjective = {};
				projetObjective.key = v4();
				projetObjective.id = 0;
				projetObjective.mea003Id = metba003.mea003Id;
				projetObjective.mea003Nome = metba003.mea003Nome;
				projetObjective.peso = 1;
				projetObjective.mea001Nome = metba003.mea001Nome;

				return projetObjective;
			});
			appendObjectives(objectivesData);

			clearErrors(names.nameOfObject);
			setLoading(false);
		}catch(err: any){
			setLoading(false);
		}
	};

	const onCallbackSelectedObjectives = (objectives: string[] | undefined) => {
		if(!objectives)
			return;
		getObjectives(objectives);	
	};

	useEffect(() => {
		appendObjectives(objectivesDefaultValue.map((objective) => ({
			id: objective[names.nameOfID],
			mea003Id: objective[names.nameOfIdSelectObjective],
			mea003Nome: objective[names.nameOfSelectObjective],
			peso: objective[names.nameOfRangeSlider],
			mea001Nome: objective[names.nameOfMap],
			key: v4()
		})));
	}, []);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
				justifyContent="center"
			>
				<RCCGrid
					container
					item
					xs={12}
					sm={12}
					md={8}
					lg={8}
				>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
					>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar Objetivo")}
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								layout="isRounded"
								size="md"
								onClick={modalSelectObjectiveRef.current?.openModalSelectObjective}
								disabled={disabled}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
								
				<RCCGrid
					container
					item
					xs={12}
					sm={12}
					md={8}
					lg={8}
				>
					{error[names.nameOfObject] && error[names.nameOfObject].message && (
						<Title color="red" size="xs">
							<RCCIcon
								name="infoCircle"
								color="var(--red)"
								style={{ marginRight: "8px"}}
							/>
							{error[names.nameOfObject].message}
						</Title>
					)}
					{fieldsObjectives.length === 0 && (
						<RCCAlert
							color="grey"
							title={translate("Nenhum objetivo adicionado")}
							description={translate("Aperte no botão acima para adicionar")}
						/>
					)}

					{!loading && fieldsObjectives && fieldsObjectives.map((objective: any, index) => (
						<RCCGrid
							container
							item
							direction="column"
							key={objective.key}
						>
							<RCCGrid
								container
								item
								alignItems="flex-start"
							>
								<input 
									type="text"
									value={objective.id}
									{...register(`${names.nameOfObject}[${index}].${names.nameOfID}`)} 
									style={{ display: "none" }} 
									readOnly
								/>
								<input 
									type="text"
									value={objective.mea003Id ? String(objective.mea003Id) : undefined}
									{...register(`${names.nameOfObject}[${index}].${names.nameOfIdSelectObjective}`)} 
									style={{ display: "none" }} 
									readOnly
								/>
								<RCCGrid
									container
									item
									justifyContent="flex-start"
									xs={10}
								>
									<Title size="md" color="purple">
										{error[`${names.nameOfObject}`] && error[`${names.nameOfObject}[${index}]`] && (
											<span style={{ display: "flex", alignItems: "center", color: "var(--red)", fontSize: "0.8rem", marginLeft: "4px"}}>
												<RCCIcon
													name="infoCircle"
													color="var(--red)"
													style={{ marginRight: "8px"}}
												/>
												{error[`${names.nameOfObject}[${index}]`].message}
											</span>
										)}
										{index + 1}. {objective.mea003Nome}
									</Title>
									<Title size="xs" color="grey">
										{`${translate("Mapa")}: ${objective.mea001Nome}`}
									</Title>
								</RCCGrid>
								<RCCGrid
									container
									item
									justifyContent="flex-end"
									xs={2}
								>
									<RCCTooltip
										color="red"
										text={translate("Excluir")}
									>
										<RCCButton
											color="transparent"
											icon={{
												name: "trash",
												color: "var(--red)"
											}}
											layout="isRounded"
											onClick={() => removeObjective(index)}
											disabled={disabled}
											tabIndex={-1}
										/>
									</RCCTooltip>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
								>
									<RCCRangeSlider
										control={control}
										name={`${names.nameOfObject}[${index}].${names.nameOfRangeSlider}`}
										color="purple"
										label=""
										defaultValue={objective.peso}
										min={1}
										max={100}
										valueLabelDisplay="on"
										styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
									/>
								</RCCGrid>
							</RCCGrid>
						</RCCGrid>											
					))}
				 {loading && <Loader/>}
				</RCCGrid>
			</RCCGrid>

			<ModalSelectObjective subtitle={subTitle} ref={modalSelectObjectiveRef} onCallbackSelectObjective={onCallbackSelectedObjectives}/>
		</>
	);
};

export default ProjetObjective;
